import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import Loader from 'react-loader-spinner';
import { PaymentProps } from '../payment/index';
import bookingApi from '../../lib/api/booking';
import { tapPaymentPubKey, isTapApplePayConfigured } from '../../config/constants';
import { checkApplePaySupport } from '../../lib/digital-wallet-support';
import GoSellElements from '../gosell/elements/GoSellElements';
import Button from '../button';
import { paymentApi } from '../../lib/api';

// Loaded on demand: the Tap Apple Pay SDK (axios, jsencrypt, ...) is only fetched on
// devices that can actually show the button.
const TapApplePay = lazy(() => import('./tap-apple-pay'));

type TapPaymentProps = PaymentProps & {
  guestName?: string;
  email?: string;
  phone?: string;
};

const TapPayment = ({ bookingGuid, paymentGuid, amount, guestName, email, phone }: TapPaymentProps) => {
  const [processing, setProcessing] = useState(false);
  const [supportsApplePay, setSupportsApplePay] = useState(false);

  useEffect(() => {
    if (isTapApplePayConfigured) {
      checkApplePaySupport().then(setSupportsApplePay);
    }
  }, []);

  const completePayment = useCallback(
    async (cardToken: string) => {
      try {
        const payment = paymentGuid
          ? await paymentApi.createTapCharge(paymentGuid, amount || 0, 'aed', cardToken)
          : await bookingApi.withTapPaymentCharge(bookingGuid, amount || 0, 'aed', cardToken);
        if (!payment) {
          // Charge failed; the API layer already toasted the error
          return;
        }
        const redirectUrl = payment.transaction?.url;
        if (redirectUrl) {
          // 3DS authentication page (card flow)
          window.location.href = redirectUrl;
          return;
        }
        // Captured without a redirect (e.g. Apple Pay) — reload into the tap_id processing state
        const url = new URL(window.location.href);
        url.searchParams.set('tap_id', payment.id);
        window.location.href = url.toString();
      } finally {
        setProcessing(false);
      }
    },
    [amount, bookingGuid, paymentGuid]
  );

  const handleApplePayToken = useCallback(
    (cardToken: string) => {
      setProcessing(true);
      return completePayment(cardToken);
    },
    [completePayment]
  );

  return (
    <div>
      {supportsApplePay && (
        <div className="apple-pay-container">
          <Suspense fallback={null}>
            <TapApplePay
              amount={amount || 0}
              guestName={guestName}
              email={email}
              phone={phone}
              onToken={handleApplePayToken}
            />
          </Suspense>
          <p className="apple-pay-divider">or pay by card</p>
        </div>
      )}
      <GoSellElements
        gateway={{
          publicKey: tapPaymentPubKey,
          language: 'en',
          supportedCurrencies: 'all',
          supportedPaymentMethods: 'all',
          notifications: 'tap-payment-notifications',
          callback: (response: any) => completePayment(response.id),
          labels: {
            cardNumber: 'Card Number',
            expirationDate: 'MM/YY',
            cvv: 'CVV',
            cardHolder: 'Name on Card',
            actionButton: 'Pay',
          },
          style: {
            base: {
              color: '#535353',
              lineHeight: '12px',
              fontFamily: 'sans-serif',
              fontSmoothing: 'antialiased',
              fontSize: '16px',
              '::placeholder': {
                color: 'rgba(0, 0, 0, 0.26)',
                fontSize: '15px',
              },
            },
            invalid: {
              color: 'red',
              iconColor: '#fa755a',
            },
          },
        }}
      />
      <p id="tap-payment-notifications" className={'result-message'}></p>
      <Button
        disabled={processing}
        onClickHandler={async () => {
          setProcessing(true);
          // @ts-ignore
          if (!(await GoSellElements.submit())) {
            setProcessing(false);
          }
        }}
        // @ts-ignore
        iconLeft={() => (processing ? <Loader type="Circles" color="#FFF" height={20} width={20} /> : null)}
        label={`Book now ${amount} AED`}
        size="medium"
        color="blue"
        fullWidth
      />
    </div>
  );
};

export default TapPayment;
